import * as React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';

import PageBase from '../../components/layout/PageBase';
import HeroBanner from '../../components/utility/HeroBanner';
import Cta from '../../components/utility/Cta';
import Faq from '../../components/pages/buying-with-torii/Faq';

function BuyingWithTorii() {
  const router = useRouter();
  const faqRef = React.useRef<any>(null);

  React.useEffect(() => {
    const { focus } = router.query;
    if (focus && focus === 'faq') {
      faqRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, []);

  const toriiSteps: any = [
    {
      content: (
        <span>
          The first step to buying a new home is deciding which towns or
          neighborhoods you want to live in, how big of a house you need, and
          what your price range is. From there, search for properties that match
          what you’re looking for on the{' '}
          <Link href="/search">
            <a target="_blank">Torii website</a>
          </Link>{' '}
          or in our mobile app (download on{' '}
          <a
            href="https://itunes.apple.com/us/app/torii-real-estate/id1362196303?mt=8"
            target="_blank"
            rel="noopener noreferrer"
          >
            iOS
          </a>{' '}
          or{' '}
          <a
            href="https://play.google.com/store/apps/details?id=com.toriiapp.android&hl=en_US"
            target="_blank"
            rel="noopener noreferrer"
          >
            Android
          </a>
          ). Once you find a property that you’re interested in, make sure to
          save it to your favorites to share with your Torii agent, co-buyer,
          friends, and family.
        </span>
      ),
      stepColor: '#5BD1F5',
      stepTitle: 'Start your search',
    },
    {
      content: (
        <span>
          If you’re not buying in cash, you’ll need to get pre-approved for a
          mortgage. The pre-approval process will help you understand what price
          range you can afford as well as make you a more competitive buyer.
          Torii has preferred lenders we have worked with for years and trust.
          For an introduction to one of our preferred lenders,{' '}
          <Link href="/get-preapproved">
            <a target="_blank">click here</a>
          </Link>
          . Learn more about pre-approvals (vs. pre-qualifications or
          pre-commitments) on the{' '}
          <a
            href="https://blog.toriihomes.com/pre-qualification-pre-approval-pre-commitment-which-is-which/"
            target="_blank"
          >
            Torii blog
          </a>
          .
        </span>
      ),
      stepColor: '#5BBCEA',
      stepTitle: 'Get pre-approved',
    },
    {
      content: (
        <span>
          Schedule a time to see your favorite properties in-person from our
          website or app. A licensed Torii real estate agent will be available
          to attend showings with you (COVID-19 CDC guidelines permitting). Our
          website will also show you which properties have upcoming open houses,
          if you would prefer to tour a home that way. We do recommend that you
          drive by the property prior to scheduling a private showing, as many
          times a buyer will decide against a property because of the
          neighborhood or immediate area.
        </span>
      ),
      stepColor: '#5BAAE0',
      stepTitle: 'Tour homes',
    },
    {
      content: (
        <span>
          Found the perfect home? <div className="spacer mobile-only" />
          Your dedicated Torii agent will work with you to put together a
          competitive offer price and contract terms that you are comfortable
          with. Easily submit your offer right from the Torii app. Your Torii
          agent will review your offer with you prior to presenting to the
          seller’s agent.
        </span>
      ),
      stepColor: '#5B9BD7',
      stepTitle: 'Make an offer',
    },
    {
      content: (
        <span>
          Once your home offer is accepted,{' '}
          <div className="spacer mobile-only" />
          we'll do the rest. We will provide you an attorney (where required),
          appraiser, and inspector, in addition to your expert agent. Your Torii
          Team will guide you through each step of the purchase, keeping you
          informed of key dates and milestones.
        </span>
      ),
      stepColor: '#5A8DD0',
      stepTitle: 'Execute contract',
    },
    {
      content: (
        <span>
          Sign the paperwork and the keys are yours. After you’ve closed on your
          new home, all that’s left to do is move-in!
        </span>
      ),
      stepColor: '#5A7EC7',
      stepTitle: 'Close and move in',
    },
  ];
  const renderSteps = toriiSteps.map((step: any, index: number) => {
    return (
      <div className="step-container" key={index}>
        <div className="first-column">
          <div className="step-index">
            <p className="step-index-text">{index + 1}</p>
          </div>
          <h2 className="step-title">{step.stepTitle}</h2>
        </div>
        <div className="step-content-container">
          <p className="step-content">{step.content}</p>
        </div>
      </div>
    );
  });
  return (
    <PageBase title="How to buy a home with Torii - A step-by-step guide">
      <div className="page-container row center-xs buying-guide">
        <HeroBanner background="buying-guide">
          <h2 className="how-it-works">How it Works</h2>
          <h3 className="helpful-guide">
            A helpful guide to buying a home with Torii
          </h3>
        </HeroBanner>
        <div
          id="torii101"
          className="col-xs-12 center-xs explainer-video-container"
        >
          <iframe
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="explainer-video"
            frameBorder={0}
            id="explainer-video"
            src="https://www.youtube.com/embed/75rOsiwNf3o?controls=0&modestbranding"
          />
        </div>
        <div className="buing-guide-inner">
          <div className="steps-box ">
            <div className="rectangle" />
            {renderSteps}
          </div>
          <div ref={faqRef} id="faq" className="faq-container">
            <Faq />
          </div>
        </div>
      </div>
      <Cta topper />
    </PageBase>
  );
}

export default BuyingWithTorii;
